/**
 * Issue: #5997
 *
 * 検索 / RAG API の薄いクライアント. lib/api.ts の `apiPost` を直接呼ぶ.
 */
import { apiPost, getToken, authenticatedFetch, resolveApiUrl } from "@/lib/api";
import type { SearchHit, SearchResult, SearchMode, RagAnswer, RagCitation } from "./types";

export interface SearchRequest {
  query: string;
  mode?: SearchMode;
  channel_ids?: string[];
  author_ids?: string[];
  has_file?: boolean;
  message_type?: string;
  date_range?: { before?: number; after?: number };
  limit?: number;
}

export async function searchMessages(req: SearchRequest): Promise<SearchResult> {
  return apiPost<SearchResult>("/v1/search", req);
}

export interface AskRequest {
  query: string;
  channel_ids?: string[];
  author_ids?: string[];
  has_file?: boolean;
  date_range?: { before?: number; after?: number };
  message_type?: string;
  top_k?: number;
  model?: string;
  region?: "global" | "japan-east";
}

export async function askAi(req: AskRequest): Promise<RagAnswer> {
  return apiPost<RagAnswer>("/v1/ask", req);
}

export interface AskStreamHandlers {
  onDelta?: (text: string) => void;
  onDone?: (final: Pick<RagAnswer, "answer" | "citations" | "confidence"> & Partial<RagAnswer>) => void;
  onError?: (err: Error) => void;
}

/**
 * SSE ストリームで /v1/ask/stream を呼ぶ.
 * 中断可能な AbortController を返す.
 */
export function askAiStream(req: AskRequest, handlers: AskStreamHandlers): { abort: () => void } {
  const controller = new AbortController();
  void runStream();
  return { abort: () => controller.abort() };

  async function runStream() {
    try {
      const headers: Record<string, string> = {
        "content-type": "application/json",
        accept: "text/event-stream",
      };
      const token = getToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await authenticatedFetch(resolveApiUrl("/v1/ask/stream"), {
        method: "POST",
        body: JSON.stringify(req),
        headers,
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const body = await res.text().catch(() => "");
        throw new Error(`ask/stream failed: status=${res.status} body=${body.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let completed = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = block.split("\n");
          let event = "delta";
          let data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) event = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trim();
          }
          if (!data) continue;
          try {
            const obj = JSON.parse(data);
            if (event === "delta" && handlers.onDelta) handlers.onDelta(String(obj.text ?? ""));
            else if (event === "done" && handlers.onDone) {
              completed = true;
              handlers.onDone({
                model: typeof obj.model === "string" ? obj.model : undefined,
                region: typeof obj.region === "string" ? obj.region : undefined,
                context_count: typeof obj.context_count === "number" ? obj.context_count : undefined,
                answer: String(obj.answer ?? ""),
                citations: Array.isArray(obj.citations) ? obj.citations : [],
                confidence: typeof obj.confidence === "number" ? obj.confidence : 0,
              });
            } else if (event === "error" && handlers.onError) {
              completed = true;
              handlers.onError(new Error(String(obj.message ?? "stream error")));
            }
          } catch {
            // partial chunk
          }
        }
      }
      if (!completed && !controller.signal.aborted) throw new Error("回答の受信中に接続が切れました。もう一度お試しください。");
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      handlers.onError?.(e as Error);
    }
  }
}

export type { SearchHit, SearchResult, SearchMode, RagAnswer, RagCitation };
